import { isPresent } from 'ts-is-present';
import { MedicalDataTypes } from '../primary-record-types';
import { PrimaryRecordAllergy, PrimaryRecordCondition } from '../primary-record-types/MedicalDataTypes';
import _uniqBy from 'lodash/uniqBy';

function roundToSignificantDigits(num: number | undefined, significantDigits: number): number | undefined {
    if (num === undefined) return undefined;

    if (num === 0) return 0;

    // Calculate the number of digits before the decimal point
    const digitsBeforeDecimal = Math.floor(Math.log10(Math.abs(num))) + 1;

    // Calculate the total number of digits to keep, including those before the decimal
    const totalDigits = digitsBeforeDecimal >= significantDigits ? digitsBeforeDecimal : significantDigits;

    // Calculate the rounding factor
    const factor = Math.pow(10, totalDigits - digitsBeforeDecimal);

    // Round the number and divide by the factor to get the final result
    return Math.round(num * factor) / factor;
}

export const resourceTypeGuards = {
    isCondition: (resource: MedicalDataTypes.PrimaryRecordResource): resource is PrimaryRecordCondition =>
        resource.type === 'condition',
    isAllergy: (resource: MedicalDataTypes.PrimaryRecordResource): resource is PrimaryRecordAllergy =>
        resource.type === 'allergy',
    isMedication: (
        resource: MedicalDataTypes.PrimaryRecordResource,
    ): resource is MedicalDataTypes.PrimaryRecordMedication => resource.type === 'medication',
    isVisit: (resource: MedicalDataTypes.PrimaryRecordResource): resource is MedicalDataTypes.PrimaryRecordVisit =>
        resource.type === 'visit',
    isVaccination: (
        resource: MedicalDataTypes.PrimaryRecordResource,
    ): resource is MedicalDataTypes.PrimaryRecordVaccination => resource.type === 'vaccination',
    isProcedure: (
        resource: MedicalDataTypes.PrimaryRecordResource,
    ): resource is MedicalDataTypes.PrimaryRecordProcedure => resource.type === 'procedure',
    isImaging: (resource: MedicalDataTypes.PrimaryRecordResource): resource is MedicalDataTypes.PrimaryRecordImaging =>
        resource.type === 'imaging',
    isLab: (resource: MedicalDataTypes.PrimaryRecordResource): resource is MedicalDataTypes.PrimaryRecordLab =>
        resource.type === 'lab',
};

export const getLabValueName = (resource: MedicalDataTypes.PrimaryRecordLab, labValue: MedicalDataTypes.LabValue) => {
    const coding = labValue.componentCoding || resource.labType;
    return coding.display;
};

const quantityToString = (quantity: MedicalDataTypes.Quantity | undefined, omitComparator?: boolean) => {
    if (!quantity) {
        return '';
    }
    return `${quantity.comparator && !omitComparator ? ' ' + quantity.comparator : ''}${roundToSignificantDigits(
        quantity.value,
        3,
    )}${quantity.unit ? ' ' + quantity.unit : ''}`;
};

export const getLabValueValueDisplay = (labValue: MedicalDataTypes.LabValue) => {
    if (labValue.value.type === 'quantity') {
        return quantityToString(labValue.value.valueQuantity);
    } else if (labValue.value.type === 'string') {
        return labValue.value.valueString;
    } else {
        return (
            labValue.value.valueCodeableConcept?.text ||
            (labValue.value.valueCodeableConcept?.coding as any[])?.find((_: any) => _.display)?.display ||
            ''
        );
    }
};

export const getReferenceRangeDisplay = (labValue: MedicalDataTypes.LabValue) => {
    const highPart = quantityToString(labValue.refHigh);
    const lowPart = quantityToString(labValue.refLow);

    if (highPart && lowPart) {
        return `${lowPart} - ${highPart}`;
    } else if (highPart) {
        return `< ${quantityToString(labValue.refHigh, true)}`;
    } else if (lowPart) {
        return `> ${quantityToString(labValue.refLow, true)}`;
    }

    return null;
};

export const isHighValue = (labValue: MedicalDataTypes.LabValue) => {
    if (labValue.value.type === 'quantity') {
        return labValue.value.valueQuantity.value! > (labValue.refHigh?.value || Number.MAX_SAFE_INTEGER);
    }
    return false;
};

export const isLowValue = (labValue: MedicalDataTypes.LabValue) => {
    if (labValue.value.type === 'quantity') {
        return labValue.value.valueQuantity.value! < (labValue.refLow?.value || Number.MIN_SAFE_INTEGER);
    }
    return false;
};

export const isOutOfRange = (labValue: MedicalDataTypes.LabValue) => {
    return isHighValue(labValue) || isLowValue(labValue);
};

export const hasFullReferenceRange = (labValue: MedicalDataTypes.LabValue) => {
    return labValue.refHigh && labValue.refLow && labValue.value.type === 'quantity';
};

export const getDisplayRange = (labValue: MedicalDataTypes.LabValue) => {
    if (!hasFullReferenceRange(labValue) || labValue.value.type !== 'quantity') {
        throw new Error('Lab value does not have a full reference range');
    }

    const value = labValue.value.valueQuantity.value!;
    const highValue = labValue.refHigh!.value!;
    const lowValue = labValue.refLow!.value!;
    const diff = highValue - lowValue;
    const defaultMin = Math.max(lowValue - diff, 0);
    const defaultMax = highValue + diff;
    return {
        min: Math.min(defaultMin, value),
        minCut: lowValue,
        max: Math.max(defaultMax, value),
        maxCut: highValue,
        value: value,
    };
};

/**
 * Dedupes labs by the following criteria:
 * - Lab type
 * - Date
 * - Value
 * - Reference range
 * - units
 */
export const dedupeLabs = <T extends MedicalDataTypes.PrimaryRecordLab>(labs: T[]) => {
    const dedupedLabHash: Record<string, T> = {};
    labs.forEach((lab) => {
        for (const value of lab.values) {
            const valueType = value.componentCoding?.code || lab.labType.code;
            const date = lab.dateTime;
            const valueDisplay = getLabValueValueDisplay(value);
            const referenceDisplay = value.value.type === 'quantity' ? getReferenceRangeDisplay(value) : '';
            const units = value.value.type === 'quantity' ? value.value.valueQuantity.unit : '';
            const key = `${valueType}-${date}-${valueDisplay}-${referenceDisplay}-${units}`;
            if (!dedupedLabHash[key]) {
                dedupedLabHash[key] = lab;
            }
        }
    });

    return _uniqBy(Object.values(dedupedLabHash), (_) => _.id);
};

type TargetLabQuantityValue = {
    value: MedicalDataTypes.LabValue<MedicalDataTypes.QuantityValue>;
    lab: MedicalDataTypes.PrimaryRecordLab;
};

const getTargetLabQuantityValues = (
    labs: MedicalDataTypes.PrimaryRecordLab[],
    code: string,
): TargetLabQuantityValue[] => {
    return labs
        .map((lab) => {
            const targetLabValue =
                lab.values.find((value) => value.componentCoding?.code === code) ||
                (lab.labType.code === code && lab.values.find((_) => !_.componentCoding));

            if (!targetLabValue || targetLabValue.value.type !== 'quantity') return null;

            return { value: targetLabValue as MedicalDataTypes.LabValue<MedicalDataTypes.QuantityValue>, lab };
        })
        .filter(isPresent);
};

export const splitLabsByUnits = (
    labs: MedicalDataTypes.PrimaryRecordLab[],
    code: string,
): MedicalDataTypes.PrimaryRecordLab[][] => {
    const unitMap = new Map<string, TargetLabQuantityValue[]>();
    const targetLabQuantityValues = getTargetLabQuantityValues(labs, code);
    targetLabQuantityValues.forEach((lab) => {
        const unit = lab.value.value.valueQuantity.unit || '';
        if (!unitMap.has(unit)) {
            unitMap.set(unit, []);
        }
        unitMap.get(unit)!.push(lab);
    });

    return Array.from(unitMap.values()).map((labs) => labs.map((_) => _.lab));
};

export type GraphDataPoint = {
    x: Date;
    y: number;
    isOutOfRange: boolean;
};

export type Range = {
    min: number;
    max: number;
};

type PrecomputedRangeRectangle = {
    x1: Date;
    x2: Date;
    y1Low: number | 'un-bounded';
    y1High: number | 'un-bounded';
    y2Low: number | 'un-bounded';
    y2High: number | 'un-bounded';
};

export type RangeRectangle = {
    x1: Date;
    x2: Date;
    y1: number;
    y2: number;
};

export type GraphData = {
    data: GraphDataPoint[];
    referenceRange: Range;
    rangeRectangles: RangeRectangle[];
    outOfRangeData: GraphDataPoint[];
    inRangeData: GraphDataPoint[];
    unit: string | null;
};

type PointRange = {
    x: Date;
    low: number | 'un-bounded';
    high: number | 'un-bounded';
};

const getPrecomputedRangeRectangles = (labValues: TargetLabQuantityValue[]) => {
    const rectangles: PrecomputedRangeRectangle[] = [];
    const sorted = labValues.sort((a, b) => new Date(a.lab.dateTime!).getTime() - new Date(b.lab.dateTime!).getTime());
    const pointRanges: PointRange[] = sorted.map((lab) => {
        const low = lab.value.refLow?.value === undefined ? 'un-bounded' : lab.value.refLow.value;
        const high = lab.value.refHigh?.value === undefined ? 'un-bounded' : lab.value.refHigh.value;
        if (low === 'un-bounded' && high === 'un-bounded') {
            return { x: new Date(lab.lab.dateTime!), low: 'un-bounded', high: 'un-bounded' };
        } else {
            return {
                x: new Date(lab.lab.dateTime!),
                low: low === null ? 'un-bounded' : low,
                high: high === null ? 'un-bounded' : high,
            };
        }
    });

    let currentRange: PrecomputedRangeRectangle | null = null;
    for (const pointRange of pointRanges) {
        const isValidRange = pointRange.low !== 'un-bounded' && pointRange.high !== 'un-bounded';
        if (!isValidRange) {
            if (currentRange !== null) {
                rectangles.push(currentRange);
                currentRange = null;
            }
            continue;
        }

        if (currentRange === null) {
            currentRange = {
                x1: pointRange.x,
                x2: pointRange.x,
                y1Low: pointRange.low,
                y1High: pointRange.high,
                y2Low: pointRange.low,
                y2High: pointRange.high,
            };
        } else {
            currentRange.x2 = pointRange.x;
            if (currentRange.y1Low !== pointRange.low || currentRange.y1High !== pointRange.high) {
                rectangles.push(currentRange);
                currentRange = {
                    x1: pointRange.x,
                    x2: pointRange.x,
                    y1Low: pointRange.low,
                    y1High: pointRange.high,
                    y2Low: pointRange.low,
                    y2High: pointRange.high,
                };
            }
        }
    }

    if (currentRange !== null) {
        rectangles.push(currentRange);
    }

    return rectangles;
};

export const getGraphData = (labs: MedicalDataTypes.PrimaryRecordLab[], code: string): GraphData => {
    const targetLabValues = getTargetLabQuantityValues(labs, code).filter((_) => !!_.lab.dateTime);
    const preComputedRangeRectangles = getPrecomputedRangeRectangles(targetLabValues);
    const allValues = [
        ...targetLabValues.map((_) => _.value.value.valueQuantity.value!),
        ...preComputedRangeRectangles.map((_) => (_.y1Low === 'un-bounded' ? null : _.y1Low)).filter(isPresent),
        ...preComputedRangeRectangles.map((_) => (_.y1High === 'un-bounded' ? null : _.y1High)).filter(isPresent),
    ];
    const maxValue = Math.max(...allValues);
    const minValue = Math.min(...allValues);
    const rangeRectanglesData = preComputedRangeRectangles
        .map((rectangle) => ({
            x1: rectangle.x1,
            x2: rectangle.x2,
            y1: rectangle.y1Low === 'un-bounded' ? minValue : rectangle.y1Low,
            y2: rectangle.y1High === 'un-bounded' ? maxValue : rectangle.y1High,
        }))
        .filter((_) => _.x1.getTime() !== _.x2.getTime());

    const data = targetLabValues
        .map<GraphDataPoint>((lab) => ({
            x: new Date(lab.lab.dateTime!),
            y: roundToSignificantDigits(lab.value.value.valueQuantity.value!, 3)!,
            isOutOfRange: isOutOfRange(lab.value),
        }))
        .sort((a, b) => a.x.getTime() - b.x.getTime());

    return {
        data,
        referenceRange: { min: minValue, max: maxValue },
        rangeRectangles: rangeRectanglesData,
        outOfRangeData: data.filter((_) => _.isOutOfRange),
        inRangeData: data.filter((_) => !_.isOutOfRange),
        unit: targetLabValues[0]?.value.value.valueQuantity.unit || null,
    };
};
